import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../../lib/supabase';
import type { FaqData } from '../../data/types';
import { FAQ_CATEGORY_LABELS } from '../../data/types';
import { Button } from '../../components/Components';
import { IfThenBlock } from '../resources/IfThenBlock';
import { SourceLink } from '../resources/SourceLink';
import { isProductSurfaceAllowed } from './funnel/marketingConfig';
import { usePrerenderSignal } from '../../utils/usePrerenderSignal';

// localStorage key for the in-flight FAQ-share referral token. Must
// match SHARE_REF_KEY in src/auth/SignUpPage.tsx - both sides write/
// read the same key so attribution survives the landing-page detour.
// When a friend taps "Get the app" they go to / (landing), not to
// /signup directly, so we can't rely on the URL ?ref=... param
// reaching SignUpPage. Instead we persist on this page's mount, and
// SignUpPage reads from localStorage on signup completion.
const SHARE_REF_KEY = 'prtcl.faq-share-ref.v1';

// ============================================
// Public FAQ Share Page
//
// Rendered for unauthenticated visitors who tap a share link from a
// friend. Mirrors the in-app FaqDetailPage layout but:
//  - No back button / no tab bar / no app chrome
//  - "Was this helpful?" card swapped for "Get the app" CTA
//  - Footer pitch with the value prop
//  - PRTCL wordmark + sign-in link in the top bar
//
// FAQ content is fetched via the get-shared-faq edge function (NOT a
// direct anon SELECT on faqs) so the auth-gated read policy on the
// faqs table stays in place. The edge function also bumps view_count
// when a valid ref token is in the URL.
// ============================================

interface SharedFaqResponse {
  faq: {
    id: string;
    question: string;
    category: string;
    data: FaqData;
    updated_at: string;
  };
  sharer_name: string | null;
  personal_message: string | null;
}

const C = {
  page: 'var(--color-bg-primary)',
  surface: 'var(--color-bg-secondary)',
  ink: 'var(--color-text-primary)',
  ink2: 'var(--color-text-secondary)',
  ink3: 'var(--color-text-tertiary)',
  hair: 'var(--color-border)',
  accent: 'var(--color-accent)',
};

const renderInlineBold = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const renderBody = (s: string | undefined): React.ReactNode => {
  if (!s) return null;
  const paragraphs = s.split(/\n\n+/);
  return paragraphs.map((para, i) => (
    <p key={i} style={{ margin: i === 0 ? '0 0 12px' : '12px 0' }}>
      {renderInlineBold(para)}
    </p>
  ));
};

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  return new Date(iso).toISOString().split('T')[0];
};

const TopBar: React.FC<{ refToken: string | null }> = ({ refToken }) => (
  <header
    style={{
      padding: '12px 18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${C.hair}`,
      background: C.page,
      position: 'sticky',
      top: 0,
      zIndex: 5,
    }}
  >
    <Link
      to="/"
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 900,
        fontSize: 18,
        letterSpacing: '-0.02em',
        color: C.ink,
        textDecoration: 'none',
      }}
    >
      PRTCL<span style={{ color: C.accent }}>.</span>
    </Link>
    {/* WEB LOCKDOWN (marketingConfig switch): no sign-in affordance on web.
        Native keeps the sign-in link for the in-app share view. */}
    {isProductSurfaceAllowed() && (
      <Link
        to={refToken ? `/signin?ref=${encodeURIComponent(refToken)}` : '/signin'}
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: C.ink2,
          textDecoration: 'none',
        }}
      >
        Sign in
      </Link>
    )}
  </header>
);

const PublicFaqSharePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const refToken = searchParams.get('ref');
  const navigate = useNavigate();

  const [data, setData] = useState<SharedFaqResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  // Tell the prerender script "ready to capture" once the get-shared-faq
  // edge function returns. Fires once per route, after Helmet has set
  // per-FAQ <title>/<meta>/<og:*> tags into the head. No-op at runtime
  // (no listener attached outside the build-time Puppeteer session).
  usePrerenderSignal(!loading && !!data);

  // Persist the ref token to localStorage on mount so attribution
  // survives the user's path through the landing page (and any of
  // its CTAs) before they finally land on /signup. Without this, a
  // Get-the-app -> landing -> sign-up flow would lose the token.
  useEffect(() => {
    if (refToken && refToken.trim()) {
      try { localStorage.setItem(SHARE_REF_KEY, refToken.trim()); }
      catch { /* localStorage unavailable - non-fatal */ }
    }
  }, [refToken]);

  useEffect(() => {
    if (!slug) {
      setErrored(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrored(false);

    // Anon-callable edge function. Bumps view_count side-effect when
    // a valid ref token is provided.
    supabase.functions
      .invoke<SharedFaqResponse>('get-shared-faq', {
        body: { slug, ref: refToken ?? '' },
      })
      .then(({ data: resp, error }) => {
        if (cancelled) return;
        if (error || !resp || !resp.faq) {
          setErrored(true);
        } else {
          setData(resp);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, refToken]);

  // CTA target is the landing page, not /signup. Reasoning: the
  // landing page sells the value prop (founder story, screenshots,
  // FAQ accordion, walkthrough) - much higher conversion for cold
  // visitors than dumping them straight on the signup form.
  // Attribution is preserved via SHARE_REF_KEY in localStorage,
  // which SignUpPage reads when the user eventually reaches it.
  const ctaHref = '/';

  if (loading) {
    return (
      <div style={{ background: C.page, minHeight: '100vh' }}>
        <TopBar refToken={refToken} />
        <div
          style={{
            padding: '60px 18px',
            display: 'flex',
            justifyContent: 'center',
            color: C.ink3,
            fontSize: 14,
          }}
        >
          Loading…
        </div>
      </div>
    );
  }

  if (errored || !data) {
    return (
      <div style={{ background: C.page, minHeight: '100vh' }}>
        <TopBar refToken={refToken} />
        <div style={{ padding: '60px 18px 24px', textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 700,
              color: C.ink,
              margin: '0 0 8px',
            }}
          >
            That article isn't available
          </h1>
          <p style={{ color: C.ink2, fontSize: 14, margin: '0 0 24px' }}>
            It may have been moved or removed. The full library lives inside the app.
          </p>
          <Button variant="primary" size="lg" onClick={() => navigate(ctaHref)}>
            Get the app
          </Button>
        </div>
      </div>
    );
  }

  const { faq, sharer_name, personal_message } = data;
  const catLabel = FAQ_CATEGORY_LABELS[faq.category as keyof typeof FAQ_CATEGORY_LABELS] || faq.category;
  const lastReviewed = faq.data?.last_reviewed || faq.updated_at;

  // Per-FAQ SEO + social-preview meta. Baked into the prerendered HTML
  // for /share/faq/:slug, so Reddit/X/iMessage preview cards (and
  // Googlebot/GPTBot) get the actual FAQ question + first-line answer
  // instead of the generic homepage card. Description is truncated to
  // ~155 chars to stay under typical preview-card limits.
  const faqTitle = `${faq.question} | PRTCL`;
  const faqDescription = (faq.data?.short_answer || faq.question)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 155);
  const faqUrl = `https://www.prtclapp.com/share/faq/${faq.id}`;

  return (
    <div style={{ background: C.page, minHeight: '100vh' }}>
      <Helmet>
        <title>{faqTitle}</title>
        <meta name="description" content={faqDescription} />
        <link rel="canonical" href={faqUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={faqUrl} />
        <meta property="og:title" content={faqTitle} />
        <meta property="og:description" content={faqDescription} />
        <meta property="og:image" content="https://www.prtclapp.com/images/og-landing-v2.png" />
        <meta property="og:site_name" content="PRTCL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={faqTitle} />
        <meta name="twitter:description" content={faqDescription} />
        <meta name="twitter:image" content="https://www.prtclapp.com/images/og-landing-v2.png" />
        {/* JSON-LD Question schema so Google can render this as a rich
            answer card. mainEntity uses the FAQ question + short answer
            directly; expanded data (if_then, go_deeper) stays on-page
            but isn't summarized in the schema to keep it focused. */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'QAPage',
          mainEntity: {
            '@type': 'Question',
            name: faq.question,
            // Google requires `text` on Question in addition to `name`.
            // Both can be the question itself since we don't store a longer form.
            text: faq.question,
            // `answerCount` is REQUIRED for QAPage rich-result eligibility.
            // Single acceptedAnswer per FAQ today, so always 1.
            answerCount: 1,
            author: {
              '@type': 'Organization',
              name: 'PRTCL',
              url: 'https://www.prtclapp.com',
            },
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.data?.short_answer || faq.question,
              // Anchor URL the rich result links to. Same as the FAQ page itself.
              url: faqUrl,
              author: {
                '@type': 'Organization',
                name: 'PRTCL',
                url: 'https://www.prtclapp.com',
              },
            },
          },
        })}</script>
      </Helmet>
      <TopBar refToken={refToken} />

      <article
        style={{
          maxWidth: 600,
          width: '100%',
          margin: '0 auto',
          padding: '24px 18px 32px',
        }}
      >
        {/* Optional sharer banner */}
        {sharer_name && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 12,
              background: C.surface,
              marginBottom: 20,
              fontSize: 14,
              color: C.ink2,
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: C.ink }}>{sharer_name}</strong> shared this with you
            {personal_message ? `: "${personal_message}"` : '.'}
          </div>
        )}

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: C.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            marginBottom: 10,
          }}
        >
          {catLabel}
        </div>

        {/* Title */}
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 900,
            fontSize: 26,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
            color: C.ink,
            margin: 0,
          }}
        >
          {faq.question}
        </h1>

        {/* Meta */}
        <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: C.ink3 }}>
          Updated {formatDate(lastReviewed)}
        </div>

        {/* Body */}
        {faq.data?.short_answer && (
          <div style={{ marginTop: 18, fontSize: 15, lineHeight: 1.65, color: C.ink2 }}>
            {renderBody(faq.data.short_answer)}
          </div>
        )}

        {/* If/Then - preserves the same chip-styled UX from the
            in-app FaqDetailPage. This is one of the highest-value
            UX moments because it gives the recipient an actionable
            decision tree, which sells the in-app experience. */}
        {faq.data?.if_then && faq.data.if_then.length > 0 && (
          <IfThenBlock items={faq.data.if_then} />
        )}

        {/* Key facts (if present) - included on public page since the
            value pitch is "this is genuinely useful" */}
        {faq.data?.go_deeper?.key_facts && faq.data.go_deeper.key_facts.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: C.ink3,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Key facts
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, color: C.ink2, fontSize: 14, lineHeight: 1.65 }}>
              {faq.data.go_deeper.key_facts.map((fact, idx) => (
                <li key={idx} style={{ marginBottom: 6 }}>
                  {fact}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sources (App Review 1.4.1). This page shows the same medical answer
            as the in-app FAQ, so it must show the same citations. It previously
            rendered key_facts but dropped go_deeper.sources, leaving the
            medical content here uncited. */}
        {faq.data?.go_deeper?.sources && faq.data.go_deeper.sources.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: C.ink3,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Sources
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {faq.data.go_deeper.sources.map((source, idx) => (
                <SourceLink key={idx} source={source} />
              ))}
            </div>
          </div>
        )}

        {/* Get-the-app CTA card */}
        <div
          style={{
            marginTop: 32,
            padding: '24px 20px',
            borderRadius: 16,
            background: C.surface,
            border: `1px solid ${C.hair}`,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: C.accent,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Get more like this
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              color: C.ink,
              margin: 0,
            }}
          >
            Your guided peptide companion.
          </h2>
          <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.55, color: C.ink2 }}>
            PRTCL walks beginners through their first peptide with confidence - guided
            reconstitution, syringe unit math from your own numbers, vial tracking, and
            answers to questions like this one. Built for first-timers, useful for everyone.
          </p>
          <ul
            style={{
              margin: '16px 0 20px',
              padding: 0,
              listStyle: 'none',
              fontSize: 13,
              color: C.ink2,
              lineHeight: 1.7,
            }}
          >
            <li>· Guided walkthrough for your first dose</li>
            <li>· Your vial and your dose, converted into syringe units</li>
            <li>· Vial inventory and dose log tracking</li>
            <li>· A library where every claim cites its source</li>
          </ul>
          <Button variant="primary" size="lg" fullWidth onClick={() => navigate(ctaHref)}>
            Get the app
          </Button>
          <p
            style={{
              marginTop: 12,
              fontSize: 12,
              color: C.ink3,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            Free to start.{isProductSurfaceAllowed() ? ' Sign in if you already have an account.' : ''}
          </p>
        </div>

        {/* Disclaimer footer */}
        <div
          style={{
            marginTop: 28,
            fontSize: 11,
            color: C.ink3,
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          PRTCL is educational. Always talk to a licensed provider about your situation.
        </div>

        {/* Legal footer (matches the landing page footer style) */}
        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: `1px solid ${C.hair}`,
            display: 'flex',
            gap: 16,
            justifyContent: 'center',
            fontSize: 12,
            color: C.ink3,
          }}
        >
          <Link to="/privacy" style={{ color: C.ink3, textDecoration: 'none' }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: C.ink3, textDecoration: 'none' }}>
            Terms
          </Link>
          <Link to="/" style={{ color: C.ink3, textDecoration: 'none' }}>
            Home
          </Link>
        </div>
      </article>
    </div>
  );
};

export default PublicFaqSharePage;
